import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';
import { RootState } from '../types';

// Define types
export enum TimeOffType {
  PTO = 'PTO',
  SICK = 'SICK',
  WFH = 'WFH',
}

export enum TimeOffStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface TimeOffEntry {
  _id: string;
  employee: {
    _id: string;
    name: string;
    email: string;
    role: string;
    /** The login linked to this employee record, when there is one. */
    userId?: string | null;
    teamName?: string | null;
    managerName?: string | null;
    managerUserId?: string | null;
    jrAeUserId?: string | null;
    department?: {
      _id: string;
      name: string;
    };
  };
  startDate: string;
  endDate: string;
  type: TimeOffType;
  hours: number;
  status: TimeOffStatus;
  notes: string;
  category?: string;
  reason?: string;
  needsReview?: boolean;
  reviewReason?: string | null;
  vpEscalatedAt?: string | null;
  vpDecisionDueAt?: string | null;
  vpClaimedBy?: string | null;
  vpClaimedAt?: string | null;
  vpDecidedBy?: string | null;
  vpDecidedAt?: string | null;
  vpDeferredBy?: string | null;
  vpDeferredTo?: string | null;
  vpDeferredAt?: string | null;
  vpDecisionSource?: 'vp' | 'hr_override' | null;
  vpOverrideByUser?: string | null;
  leadsApproved?: boolean | null;
  originalCalendarSubject?: string;
  originalCalendarBody?: string;
  originalCalendarCategories?: string;
  originalCalendarShowAs?: string;
  originalCalendarIsAllDay?: boolean;
  originalCalendarLocation?: string;
  originalCalendarStart?: string;
  originalCalendarEnd?: string;
  originalCalendarTimeZone?: string;
  reconciled?: boolean;
  isFromCalendar?: boolean;
  isRecurring?: boolean;
  outlookEventId?: string;
  createdAt: string;
  updatedAt: string;
}

/** A person on a WFH approval route. */
export interface RoutePerson {
  id: string | null;
  name: string;
}

export type RouteDelivery = 'sending' | 'sent' | 'delivered' | 'failed' | 'unknown';

/**
 * Where a WFH request is in its approval route (GET /time-off/:id/approval-route;
 * backend utils/wfhApprovalRoute.ts). Null for other request types.
 */
export interface WfhApprovalRoute {
  stage: 'auto_approved' | 'awaiting_employee' | 'with_vps' | 'with_hr' | 'approved' | 'declined' | 'withdrawn' | 'canceled';
  employee: RoutePerson;
  /** The request row's updated_at on the server, to tell when a screen's copy is stale. */
  entryUpdatedAt: string | null;
  reviewers: { pool: 'sales' | 'vp' | 'fallback'; people: RoutePerson[] } | null;
  reviewersProblem: string | null;
  holder: {
    state: 'unclaimed' | 'claimed' | 'deferred';
    person: RoutePerson | null;
    by: RoutePerson | null;
    at: string | null;
    /** The deferral target is no longer one of the reviewers. */
    stale: boolean;
    openToAll: boolean;
  } | null;
  escalatedAt: string | null;
  deadline: { dueAt: string; urgent: boolean; overdue: boolean } | null;
  decision: {
    outcome: 'approved' | 'declined';
    withLeads: boolean | null;
    /**
     * hybrid_schedule: approved as it came in, and the employee is on a hybrid
     * schedule now. on_arrival: approved as it came in; why isn't recorded.
     * recurring: part of a recurring series HR logged. in_app: decided from
     * Attendance before the employee sent it to the VPs.
     */
    source: 'vp' | 'hr_override' | 'hybrid_schedule' | 'on_arrival' | 'recurring' | 'in_app' | 'other';
    by: RoutePerson | null;
    at: string | null;
  } | null;
  notices: Array<{
    kind:
      | 'employee_request'
      | 'initial'
      | 'daily_digest'
      | 'due_soon'
      | 'overdue'
      | 'overdue_escalation'
      | 'overdue_escalation_copy'
      | 'vp_defer'
      | 'vp_decision_notice'
      | 'hr_closure_notice'
      | 'employee_result'
      | 'rescind_notice';
    at: string;
    recipients: Array<RoutePerson & { delivery: RouteDelivery }>;
  }>;
}

export interface EmailContent {
  to: string;
  subject: string;
  content: string;
}

// Define a new interface for the API response
export interface EmailApiResponse {
  email: EmailContent;
}

export interface TimeOffState {
  selectedTimeOff: TimeOffEntry | null;
  filter: {
    employeeId: string | null;
    departmentId: string | null;
    type: TimeOffType | null;
    status: TimeOffStatus | null;
    startDate: string | null;
    endDate: string | null;
  };
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

// Initial state
const initialState: TimeOffState = {
  selectedTimeOff: null,
  filter: {
    employeeId: null,
    departmentId: null,
    type: null,
    status: null,
    startDate: null,
    endDate: null,
  },
  sortBy: 'startDate',
  sortOrder: 'desc',
};

// Create the slice
const timeOffSlice = createSlice({
  name: 'timeOff',
  initialState,
  reducers: {
    setSelectedTimeOff: (state, action: PayloadAction<TimeOffEntry | null>) => {
      state.selectedTimeOff = action.payload;
    },
    setEmployeeFilter: (state, action: PayloadAction<string | null>) => {
      state.filter.employeeId = action.payload;
    },
    setDepartmentFilter: (state, action: PayloadAction<string | null>) => {
      state.filter.departmentId = action.payload;
    },
    setTypeFilter: (state, action: PayloadAction<TimeOffType | null>) => {
      state.filter.type = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<TimeOffStatus | null>) => {
      state.filter.status = action.payload;
    },
    setDateRangeFilter: (
      state,
      action: PayloadAction<{ startDate: string | null; endDate: string | null }>
    ) => {
      state.filter.startDate = action.payload.startDate;
      state.filter.endDate = action.payload.endDate;
    },
    clearFilters: (state) => {
      state.filter = initialState.filter;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
  },
});

// Export actions and reducer
export const {
  setSelectedTimeOff,
  setEmployeeFilter,
  setDepartmentFilter,
  setTypeFilter,
  setStatusFilter,
  setDateRangeFilter,
  clearFilters,
  setSortBy,
  setSortOrder,
} = timeOffSlice.actions;
export default timeOffSlice.reducer;

// Selectors
export const selectSelectedTimeOff = (state: RootState) => state.timeOff.selectedTimeOff;
export const selectTimeOffFilter = (state: RootState) => state.timeOff.filter;
export const selectTimeOffSortBy = (state: RootState) => state.timeOff.sortBy;
export const selectTimeOffSortOrder = (state: RootState) => state.timeOff.sortOrder;

// API endpoints
export const timeOffApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTimeOffEntries: builder.query<TimeOffEntry[], void>({
      query: () => '/time-off',
      transformResponse: (response: { success: boolean; data: TimeOffEntry[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'TimeOff' as const, id: _id })),
              { type: 'TimeOff', id: 'LIST' },
            ]
          : [{ type: 'TimeOff', id: 'LIST' }],
    }),
    getTimeOffEntryById: builder.query<TimeOffEntry, string>({
      query: (id) => `/time-off/${id}`,
      transformResponse: (response: { success: boolean; data: TimeOffEntry }) => response.data,
      providesTags: (result, error, id) => [{ type: 'TimeOff', id }],
    }),
    // Shares the entry's tag, so every time-off write (which invalidates
    // 'TimeOff') refetches where the request now sits.
    getWfhApprovalRoute: builder.query<WfhApprovalRoute | null, string>({
      query: (id) => `/time-off/${id}/approval-route`,
      transformResponse: (response: { success: boolean; data: WfhApprovalRoute | null }) => response.data,
      providesTags: (result, error, id) => [{ type: 'TimeOff', id }],
    }),
    getFilteredTimeOffEntries: builder.query<
      TimeOffEntry[],
      {
        employeeId?: string;
        departmentId?: string;
        type?: TimeOffType;
        status?: TimeOffStatus;
        startDate?: string;
        endDate?: string;
        /** Upper bound on start_date — with startDate, "starts within window". */
        startBefore?: string;
        /** Lower bound on end_date — "still active on/after this date". */
        endAfter?: string;
      }
    >({
      query: (params) => ({
        url: '/time-off',
        params,
      }),
      transformResponse: (response: { success: boolean; data: TimeOffEntry[] }) => response.data,
      providesTags: [{ type: 'TimeOff', id: 'LIST' }],
    }),
    addTimeOffEntry: builder.mutation<TimeOffEntry, Partial<TimeOffEntry>>({
      query: (timeOff) => ({
        url: '/time-off',
        method: 'POST',
        body: timeOff,
      }),
      transformResponse: (response: { success: boolean; data: TimeOffEntry }) => response.data,
      // A time-off write can change employee balances as well as attendance.
      // Invalidate the whole aggregate, including cached detail/summary views;
      // department summaries also subscribe to the TimeOff list tag.
      invalidatesTags: ['TimeOff', 'Employee'],
    }),
    updateTimeOffEntry: builder.mutation<TimeOffEntry, { id: string; timeOff: Partial<TimeOffEntry> }>({
      query: ({ id, timeOff }) => ({
        url: `/time-off/${id}`,
        method: 'PUT',
        body: timeOff,
      }),
      transformResponse: (response: { success: boolean; data: TimeOffEntry }) => response.data,
      invalidatesTags: ['TimeOff', 'Employee'],
    }),
    deleteTimeOffEntry: builder.mutation<void, string>({
      query: (id) => ({
        url: `/time-off/${id}`,
        method: 'DELETE',
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: ['TimeOff', 'Employee'],
    }),
    generateEmployeeEmail: builder.query<EmailApiResponse, string>({
      query: (id) => `/time-off/employee/${id}/email`,
      transformResponse: (response: { success: boolean; data: EmailApiResponse }) => response.data,
    }),
  }),
});

// Export hooks
export const {
  useGetTimeOffEntriesQuery,
  useGetTimeOffEntryByIdQuery,
  useGetWfhApprovalRouteQuery,
  useGetFilteredTimeOffEntriesQuery,
  useAddTimeOffEntryMutation,
  useUpdateTimeOffEntryMutation,
  useDeleteTimeOffEntryMutation,
  useGenerateEmployeeEmailQuery,
} = timeOffApiSlice;
